import { Backend } from '../../../api/Backend';
import { IAssetAction, IProgramSummary, IScheduleSummary, ISeasonSummary } from '../../../api/models/AndromeTV.types';
import { getTime } from '../../utility/DateFormatter';
import { ActionMethod, AssetType } from '../../../api/models/Shared.types';
import { EpgUtil } from '../../utility/EpgUtil';
import { CmsCardIcon } from '../../../components/cms/CmsCardIcon';
import { ChannelModel } from '../../../api/EpgApi.types';
import { Recording } from '../../../api/user/RecordingApi.types';
import { Utils } from '@lightningjs/sdk';
import { GlobalSettingsKeys } from '../../../api/models/GlobalSettingsKeys';
import { GuideTranslationKeys } from '../../../api/models/TranslationKeys';

export class EpgGuideProgramDataSource {
  public readonly backend: Backend;
  private schedule: IScheduleSummary;
  private program: IProgramSummary | undefined;
  private start: Date | undefined; // used for debugging
  private end: Date | undefined; // used for debugging
  private _miniDetailDataPromis: Promise<any>;
  private season: ISeasonSummary | undefined;
  private _iconPromise: Promise<any[]> | undefined;
  private channelModel: ChannelModel;
  private icons: any[] = [];
  private recording: Recording | undefined;

  constructor(
    backend: Backend,
    schedule: IScheduleSummary,
    channel: ChannelModel,
    program?: IProgramSummary,
    season?: ISeasonSummary
  ) {
    this.backend = backend;
    this.schedule = schedule;
    this.program = program;
    this.season = season;
    this.channelModel = channel;
    this._miniDetailDataPromis = this._miniDetailDataPromise();
  }

  get startTime() {
    this.start = new Date(Number(this.schedule.published.start) * 1000); // used for debugging
    return Number(this.schedule.published.start) * 1000;
  }

  get endTime() {
    this.end = new Date(Number(this.schedule.published.end) * 1000); // used for debugging
    return Number(this.schedule.published.end) * 1000;
  }

  get programId(): string | undefined {
    return this.program?.id;
  }

  get channelId(): string {
    return this.channelModel.channelId;
  }

  get scheduleId() {
    return this.schedule.id;
  }

  get image(): string | undefined {
    return this.program?.posterImage;
  }

  get title(): string | undefined {
    // if program is adult then hide title
    if (this.program?.adult === false) {
      return this.program?.title;
    }
    return this.backend.translations.forKey(GuideTranslationKeys.guide_adult_title);
  }

  get shortSynopsis(): string | undefined {
    return this.program?.shortSynopsis;
  }

  get startTimeString(): string {
    return getTime(new Date(Number(this.schedule.published.start) * 1000));
  }

  get action(): IAssetAction {
    return {
      method: ActionMethod.DETAIL,
      type: AssetType.SCHEDULE,
      id: this.scheduleId,
      extra: undefined,
    };
  }

  get miniDetailData(): Promise<any> {
    return this._miniDetailDataPromis;
  }

  _miniDetailDataPromise(): Promise<any> {
    return this.getEpgIcons().then(icons => {
      const data = {
        ...this.program,
        schedule: this.schedule,
        type: AssetType.PROGRAM,
        synopsis: this.shortSynopsis,
        image: this.image,
        season: this.season?.season,
        recordingIcons: icons,
      };
      return data;
    });
  }

  get genreStrings() {
    const arr: string[] = this.program ? (this.program.tags ? this.program.tags : []) : [];
    return arr.map(el => el.toLowerCase());
  }

  _loadRecording(programId: string, channelId: string): Promise<Recording | undefined> {
    return this.backend.cachedRecordingsApi.recordingForProgram(this.backend, programId, channelId).then(recording => {
      this.recording = recording;
      return recording;
    });
  }

  getEpgIcons() {
    if (this._iconPromise) {
      return this._iconPromise;
    }
    this._iconPromise = new Promise(resolve => {
      if (this.program && this.programId && this.schedule) {
        this._addAccessibilityIcons(this.schedule.accessibilityOptions);
        return this._loadRecording(this.programId, this.channelId).then(recording => {
          if (recording) {
            this._addRecordingIcons(recording);
          }
          this._addPlayIcon(recording);
          resolve(this.icons);
        });
      } else {
        resolve(this.icons);
      }
    });
    return this._iconPromise;
  }

  _addAccessibilityIcons(tags: string[] = []) {
    // comment next line to enable icons without global setting
    if (!this.backend.globalSettings.boolValue(GlobalSettingsKeys.showAccessibilityIcons)) return;
    // manual if test checking to ensure correct order
    if (tags.includes('SUBTITLES')) {
      this.icons.push({
        type: CmsCardIcon,
        icon: Utils.asset('images/accessibility/subtitles.png'),
      });
    }
    if (tags.includes('AUDIO_DESCRIPTION')) {
      this.icons.push({
        type: CmsCardIcon,
        icon: Utils.asset('images/accessibility/audio_description.png'),
      });
    }
    if (tags.includes('SIGN_LANGUAGE')) {
      this.icons.push({
        type: CmsCardIcon,
        icon: Utils.asset('images/accessibility/sign_language.png'),
      });
    }
  }

  _addRecordingIcons(recording: Recording) {
    const recIcons = EpgUtil.getRecordingIcons(recording);
    recIcons.forEach(recIcon => {
      this.icons.push({
        type: CmsCardIcon,
        icon: recIcon.src,
      });
    });
  }

  _addPlayIcon(recording: Recording | undefined) {
    const play = EpgUtil.getPlayIcon(this.backend, this.schedule, recording, this.channelModel, undefined, false);
    if (play) {
      this.icons.push({
        type: CmsCardIcon,
        icon: Utils.asset(play),
      });
    }
  }

  iconsToIconDef(arr: any[]) {
    return arr.map(el => {
      return { src: el.src };
    });
  }

  get adult() {
    return this.program?.adult;
  }
}
